import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

/** Quanto cada clique nas setas desloca: pouco menos que a largura visível, para manter contexto. */
const FRACAO_PASSO = 0.8

/**
 * Quadro de colunas do Pipeline com rolagem horizontal utilizável (relato de teste, 19/10/2026:
 * "só está sendo possível rolar pelas setas do teclado"). Antes, a barra horizontal ficava no pé
 * do quadro — abaixo da coluna mais alta, fora da tela — e quase invisível (cinza-claro sobre o
 * fundo). Agora:
 *  - a altura do quadro é limitada à tela: a barra horizontal fica sempre à vista e as colunas
 *    rolam na vertical dentro do próprio quadro;
 *  - a barra é mais grossa e contrastada;
 *  - setas ‹ › rolam entre as colunas (desabilitadas na borda; somem quando tudo cabe).
 * A rolagem nativa continua: Shift + roda do mouse, touchpad e, no arrastar de um card, a
 * rolagem automática do navegador perto das bordas.
 */
export const QuadroRolavel: FC<{ children: ReactNode; 'aria-label': string }> = ({
  children,
  'aria-label': ariaLabel,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [podeEsquerda, setPodeEsquerda] = useState(false)
  const [podeDireita, setPodeDireita] = useState(false)

  const medir = useCallback(() => {
    const el = ref.current
    if (!el) return
    setPodeEsquerda(el.scrollLeft > 1)
    setPodeDireita(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    medir()
    const el = ref.current
    if (!el) return
    el.addEventListener('scroll', medir, { passive: true })
    window.addEventListener('resize', medir)
    // Colunas e cards chegam depois da carga: remede quando o conteúdo muda de tamanho.
    const observador = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null
    observador?.observe(el)
    if (el.firstElementChild) observador?.observe(el.firstElementChild)
    return () => {
      el.removeEventListener('scroll', medir)
      window.removeEventListener('resize', medir)
      observador?.disconnect()
    }
  }, [medir])

  const rolar = (sentido: 1 | -1) => {
    const el = ref.current
    if (!el) return
    el.scrollBy({ left: sentido * Math.max(240, el.clientWidth * FRACAO_PASSO), behavior: 'smooth' })
  }

  const mostrarSetas = podeEsquerda || podeDireita

  return (
    <Box>
      {mostrarSetas && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mb: 0.5 }}>
          <Tooltip title="Colunas anteriores">
            <span>
              <IconButton
                size="small"
                aria-label="Rolar o pipeline para a esquerda"
                onClick={() => rolar(-1)}
                disabled={!podeEsquerda}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Próximas colunas">
            <span>
              <IconButton
                size="small"
                aria-label="Rolar o pipeline para a direita"
                onClick={() => rolar(1)}
                disabled={!podeDireita}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      <Box
        ref={ref}
        role="region"
        aria-label={ariaLabel}
        // Focável: com o foco no quadro, as setas do teclado rolam (o comportamento que já existia).
        tabIndex={0}
        data-testid="quadro-rolavel"
        sx={{
          display: 'flex',
          gap: 1.5,
          overflow: 'auto',
          pb: 1,
          // Altura limitada à tela (cabeçalho, título e controles acima) para a barra horizontal
          // ficar sempre visível; em telas baixas, um mínimo para não espremer as colunas.
          maxHeight: 'max(360px, calc(100vh - 270px))',
          scrollbarWidth: 'auto',
          scrollbarColor: '#9A9AA5 #E2E2E7',
          '&::-webkit-scrollbar': { height: 14, width: 12 },
          '&::-webkit-scrollbar-track': { background: '#E2E2E7', borderRadius: 7 },
          '&::-webkit-scrollbar-thumb': {
            background: '#9A9AA5',
            borderRadius: 7,
            border: '3px solid #E2E2E7',
          },
          '&::-webkit-scrollbar-thumb:hover': { background: '#7A7A85' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export default QuadroRolavel
